/**
 * Pure donut-chart geometry for the Spend Insights view.
 *
 * The donut is drawn as a single SVG `<circle>` per segment, all sharing one
 * radius/circumference, using `stroke-dasharray` (visible arc + the rest as a
 * gap) and a rotating `stroke-dashoffset` so each segment starts where the
 * previous one ended. Keeping the maths here — free of React and the DOM —
 * lets us unit-test the arc/percentage computation directly.
 *
 * Design notes:
 *  - Inputs are the same decimal-string category totals the service returns
 *    ({@link InsightCategory}); we take absolute values (spend magnitude) and
 *    ignore non-finite / non-positive entries so a bad value never distorts the
 *    ring or throws.
 *  - `percent` is the TRUE share of the total (sums to ~100 across drawn
 *    segments) and is what callers should display — it is never inflated by the
 *    minimum-arc rule below.
 *  - `dashLength` is the DRAWN arc length. To keep a tiny non-zero slice
 *    visible we clamp it to a small minimum (mirroring the existing
 *    `Math.max(4, …)` bar rule), but this only affects the visual arc, not the
 *    reported `percent`/totals. Offsets are laid out from the true fractions so
 *    the segments still tile the ring in order.
 *  - Empty input (or all-zero/negative) yields no segments and a zero total, so
 *    the caller can skip rendering the donut entirely (divide-by-zero guarded).
 */

/** A minimal category shape: a label and a decimal-string total. */
export interface DonutInput {
  readonly category: string;
  readonly total: string;
}

/** One computed donut segment, ready to render as an SVG circle. */
export interface DonutSegment<T extends DonutInput = DonutInput> {
  /** The source category row (label + total). */
  readonly item: T;
  /** Absolute numeric spend magnitude parsed from `total`. */
  readonly value: number;
  /** True share of the total, 0–100 (sums to ~100 across segments). */
  readonly percent: number;
  /** Drawn arc length in circumference units (clamped to a min for tiny slices). */
  readonly dashLength: number;
  /** `stroke-dashoffset` positioning this segment's start on the ring. */
  readonly dashOffset: number;
}

/** The full donut model: segments + the numeric total (for guards/labels). */
export interface DonutModel<T extends DonutInput = DonutInput> {
  readonly segments: readonly DonutSegment<T>[];
  /** Sum of all segment magnitudes; `0` when there is nothing to draw. */
  readonly total: number;
}

/** Parse a decimal-string total to a non-negative magnitude (0 when invalid). */
export function toMagnitude(total: string): number {
  const value = Number(total);
  return Number.isFinite(value) ? Math.abs(value) : 0;
}

/**
 * Compute donut segments for a set of category totals.
 *
 * @param items         Category rows (label + decimal-string total).
 * @param circumference The circle circumference the arcs are laid out on
 *                      (dash lengths/offsets are in these units). Defaults to
 *                      100 so `dashLength` reads as "percent of the ring".
 * @param minArc        Minimum drawn arc (same units) for a non-zero slice, so
 *                      very small shares stay visible. Defaults to 0 (no clamp);
 *                      callers pass a small value (e.g. circumference * 0.01).
 */
export function computeDonut<T extends DonutInput>(
  items: readonly T[],
  circumference = 100,
  minArc = 0,
): DonutModel<T> {
  const magnitudes = items.map((item) => ({ item, value: toMagnitude(item.total) }));
  const total = magnitudes.reduce((sum, m) => sum + m.value, 0);

  // Divide-by-zero / empty guard: nothing to draw.
  if (total <= 0) {
    return { segments: [], total: 0 };
  }

  const drawable = magnitudes.filter((m) => m.value > 0);

  // Offsets are laid from the TRUE fractions so segments tile the ring in order
  // and the numbers stay honest; the min-arc clamp only widens the drawn dash.
  let cumulative = 0;
  const segments = drawable.map(({ item, value }) => {
    const fraction = value / total;
    const percent = fraction * 100;
    const trueArc = fraction * circumference;
    const dashLength = minArc > 0 ? Math.max(minArc, trueArc) : trueArc;
    // stroke-dashoffset convention: negative shift moves the dash start
    // clockwise around the ring by the accumulated (true) arc length.
    const dashOffset = -cumulative;
    cumulative += trueArc;
    return { item, value, percent, dashLength, dashOffset };
  });

  return { segments, total };
}
